import { Button } from '@/components/ui/button';
import { Sheet, SheetTrigger, SheetContent } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PanelLeft, PanelLeftClose, PanelLeftOpen } from "lucide-react"
import { Menu } from '@/types/menu';
import { cn } from '@/utils/cn';
import { NavLink } from 'react-router-dom';
import { useMarca } from '@/components/providers/MarcaProvider';
import {ReactComponent as Logo} from '../../assets/img/logo-transparent.svg';

type SidebarProps = {
  closeMenu?: () => void;
  openMenu?: () => void;
  onToggle?: () => void;
  menus: Menu[];
  opened?: boolean;
  tenant?: string;
}

const Marca = ({ compacta }: { compacta?: boolean }) => {
  const { titulo, logo } = useMarca();
  return (
    <div className={cn("flex h-16 shrink-0 items-center gap-2.5 border-b", compacta ? "justify-center" : "px-5")}>
      {logo
        ? <img src={logo} alt="" className={cn("shrink-0 object-contain", compacta ? "h-7 w-7" : "h-8 w-auto max-w-[7rem]")} />
        : <Logo className='h-7 w-7 shrink-0 fill-primary' />}
      {!compacta && <span className="truncate text-base font-semibold tracking-tight text-foreground">{titulo}</span>}
    </div>
  );
};

type ItensProps = { menus: Menu[]; tenant?: string; compacta?: boolean; onSelect?: () => void };

const Itens = ({ menus, tenant, compacta, onSelect }: ItensProps) => (
  <nav aria-label="Menu principal" className={cn("flex flex-1 flex-col gap-0.5 overflow-y-auto py-4", compacta ? "items-center px-2" : "px-3")}>
    {menus.map((menu, i) => {
      const novaSecao = menu.secao && menu.secao !== menus[i - 1]?.secao;
      const link = (
        <NavLink
          to={`/${tenant}${menu.href}`}
          end={menu.href === '/'}
          onClick={onSelect}
          className={({ isActive }) => cn(
            "flex h-9 items-center gap-3 rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            compacta ? "w-9 justify-center" : "w-full px-2.5",
            isActive
              ? "bg-primary/10 font-medium text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
        >
          {menu.icon("h-4 w-4 shrink-0")}
          <span className={compacta ? "sr-only" : undefined}>{menu.title}</span>
        </NavLink>
      );

      return (
        <div key={menu.href} className={compacta ? undefined : "w-full"}>
          {novaSecao && (compacta
            ? <div className="mx-auto my-3 h-px w-6 bg-border" />
            : <p className="mb-1 mt-5 px-2.5 text-xs font-medium text-muted-foreground/80">{menu.secao}</p>)}
          {compacta ? (
            <Tooltip>
              <TooltipTrigger asChild><span className="block">{link}</span></TooltipTrigger>
              <TooltipContent side="right">{menu.title}</TooltipContent>
            </Tooltip>
          ) : link}
        </div>
      );
    })}
  </nav>
);

export const SidebarMobile = (props: SidebarProps) => (
  <Sheet open={props.opened} onOpenChange={props.opened ? props.closeMenu : undefined}>
    <SheetTrigger asChild>
      <Button size="icon" variant="outline" className="sm:hidden" onClick={props.openMenu}>
        <PanelLeft className="h-5 w-5" />
        <span className="sr-only">Abrir menu</span>
      </Button>
    </SheetTrigger>
    <SheetContent side="left" className="flex w-72 flex-col p-0">
      <Marca />
      <Itens menus={props.menus} tenant={props.tenant} onSelect={props.closeMenu} />
    </SheetContent>
  </Sheet>
);

export const Sidebar = (props: SidebarProps) => {
  const compacta = !props.opened;

  return (
    <aside data-state={props.opened ? 'open' : 'closed'} className={cn("fixed inset-y-0 left-0 z-10 hidden flex-col border-r bg-background sm:flex", compacta ? "w-14" : "w-64")}>
      <Marca compacta={compacta} />
      <Itens menus={props.menus} tenant={props.tenant} compacta={compacta} />
      <div className={cn("border-t p-2", compacta && "flex justify-center")}>
        <button
          type="button"
          onClick={props.onToggle}
          aria-label={compacta ? "Expandir menu" : "Recolher menu"}
          className={cn(
            "flex h-9 items-center gap-3 rounded-md text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            compacta ? "w-9 justify-center" : "w-full px-3.5"
          )}
        >
          {compacta ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /> Recolher menu</>}
        </button>
      </div>
    </aside>
  );
}
